import React, { useCallback, useEffect, useState } from 'react';
import {
	Inventory,
	NoteType,
	SharedLocation,
	TaskInventory,
	TaskNote,
	WayPoint,
	AutoCompleteAddress,
	BringgGeocodedAddress,
	CustomUnitsDefinition,
	Scan,
	OrderPageTabs
} from '@bringg/types';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import CardWrapper from '../../components/card/card-wrapper';
import Row from '../../components/row/row';
import Col from '../../components/col/col';
import { get as _get, partition as _partition, noop as _noop } from 'lodash';
import WayPointTimes from '../../components/waypoint-times/waypoint-times';
import CustomerContact from '../../components/customer-contact/customer-contact';
import Modal from '../../components/modal/modal';
import WayPointCardForm from './waypoint-card-form/waypoint-card-form';
import { MapData } from '../../components/mapbox/mapbox';
import Label from '../../components/label/label';
import WaypointCardTabs from './waypoint-card-tabs';
import { PickupDropoffOption, TaskStatus } from '@bringg/types/types/task';
import { TimezoneContext } from './hooks/use-timezone';
import TooltipWrapper from 'components/tooltip/tooltip';
import { validateLocation } from '../../services/validation-rules';
import {
	OrderPageConfiguration,
	Translations,
	WaypointStatusClass,
	WindowCommunicationService
} from './waypoint-card.consts';
import { PromisedTimeWindow } from 'components/task-promised-time-window/task-promised-time-window';
import { checkForRejectedInventories, getFormattedAddress, tabPaneTitle } from './waypoint-card.utils';
import { DefaultDateFormat } from '../../types/common.consts';

interface WayPointCardProps {
	wayPoint: WayPoint;
	translations: Translations;
	numberOfStops?: number;
	onSave?: (wayPoint: WayPoint) => void;
	onDelete?: (wayPointId: number) => void;
	onQuantityChange?: (massQuantityUpdate: TaskInventory[]) => void;
	onInventoriesChange?: (inventories: Inventory[]) => void;
	onNoteAdded?: (note: string) => void;
	timeZone?: string;
	dateFormat?: string;
	hourFormat: string;
	customUnitsDefinition?: CustomUnitsDefinition;
	taskNotes?: TaskNote[];
	scans?: Scan[];
	taskInventories?: TaskInventory[];
	sharedLocations?: SharedLocation[];
	mapData?: MapData;
	fetchAddresses?: (query: string) => Promise<AutoCompleteAddress[]>;
	getPlaceDetails?: (placeId: string) => Promise<BringgGeocodedAddress>;
	configuration: OrderPageConfiguration;
	setMapMarker: (marker) => void;
	allInventoriesInTask?: TaskInventory[];
	isTaskActive?: boolean;
	taskInventoryScanMap?: Map<number, Scan>;
	doNotMultipleWeightByQuantity?: boolean;
	shouldShowLinkSourceTask?: boolean;
	promisedTimeWindow?: PromisedTimeWindow;
	taskStatus: TaskStatus;
	defaultTab?: OrderPageTabs;
	WindowCommunicationService: WindowCommunicationService;
	showVerificationCode?: boolean;
	editBaseEtosAsEtos?: boolean;
	phoneNumberFormatter?: (phoneNumber: string) => string;
}

const WayPointCard: React.FC<WayPointCardProps> = ({
	wayPoint,
	numberOfStops = 1,
	onSave = _noop,
	onDelete = _noop,
	onQuantityChange = _noop,
	onInventoriesChange = _noop,
	onNoteAdded = _noop,
	timeZone,
	taskNotes,
	scans,
	dateFormat = DefaultDateFormat,
	hourFormat,
	taskInventories,
	sharedLocations,
	mapData,
	translations,
	fetchAddresses = () => Promise.resolve([]),
	getPlaceDetails = () => Promise.resolve({}),
	configuration,
	setMapMarker,
	allInventoriesInTask,
	customUnitsDefinition,
	isTaskActive,
	taskInventoryScanMap,
	doNotMultipleWeightByQuantity,
	shouldShowLinkSourceTask,
	promisedTimeWindow,
	taskStatus,
	defaultTab,
	WindowCommunicationService,
	showVerificationCode,
	editBaseEtosAsEtos,
	phoneNumberFormatter
}: WayPointCardProps) => {
	const [toolTipText, setToolTipText] = useState(translations.copyCoordinates);
	const [showModalEditForm, setShowModalEditForm] = useState(false);
	const [wayPointData, setWayPointData] = useState(wayPoint);
	const [waypointStatusData, setWaypointStatusData] = useState({ className: '', title: '' });
	const [hasRejectedInventories, setHasRejectedInventories] = useState(false);
	const { edit: canEdit, editInventoryQuantity, editDimension } = configuration.permissions;
	const editable = canEdit && isTaskActive;
	const canAddNotes = canEdit;
	const timezoneForDisplay = configuration.flags.useTeamTimezone && timeZone;

	const copyCoordinatesToClipboard = async () => {
		await navigator.clipboard.writeText(`${wayPointData.lat},${wayPointData.lng}`);
		setToolTipText(translations.coordinatesCopied);
		setTimeout(() => {
			setToolTipText(translations.copyCoordinates);
		}, 2000);
	};

	useEffect(() => {
		setWayPointData(wayPoint);
	}, [wayPoint]);

	const hideModalForm = () => {
		setShowModalEditForm(false);
	};

	const onFormSave = (wayPoint: WayPoint) => {
		hideModalForm();
		setWayPointData(wayPoint);
		onSave(wayPoint);
	};

	const onFormDelete = (wayPointId: number) => {
		hideModalForm();
		onDelete(wayPointId);
	};

	const onEditClick = event => {
		setShowModalEditForm(true);
		event.preventDefault();
	};

	const getWaypointStatus = useCallback((): void => {
		switch (taskStatus) {
			case TaskStatus.Cancelled:
				wayPoint.checkout_time
					? setWaypointStatusData({
							className: WaypointStatusClass.PARTIALLY_FULFILLED,
							title: 'Partially fulfilled'
					  })
					: setWaypointStatusData({ className: WaypointStatusClass.CANCELED, title: translations.canceled });
				break;

			case TaskStatus.Done:
				hasRejectedInventories
					? setWaypointStatusData({
							className: WaypointStatusClass.PARTIALLY_FULFILLED,
							title: translations.partially_fulfilled
					  })
					: setWaypointStatusData({
							className: WaypointStatusClass.FULFILLED,
							title: translations.fulfilled
					  });
				break;
		}
	}, [
		hasRejectedInventories,
		taskStatus,
		translations.canceled,
		translations.fulfilled,
		translations.partially_fulfilled,
		wayPoint.checkout_time
	]);

	useEffect(() => {
		setWayPointData(wayPoint);
		if (taskInventories?.length) {
			setHasRejectedInventories(checkForRejectedInventories(taskInventories));
		}
	}, [taskInventories, wayPoint]);

	useEffect(() => {
		getWaypointStatus();
	}, [getWaypointStatus, hasRejectedInventories]);

	const getLabel = useCallback(() => {
		const { pickup_dropoff_option } = wayPointData;

		switch (pickup_dropoff_option) {
			case PickupDropoffOption.PICKUP:
				return <Label text={translations.collectText} size="small" className="orange" />;

			case PickupDropoffOption.DROPOFF:
				return <Label text={translations.deliverText} size="small" className="green" />;

			case PickupDropoffOption.BOTH:
				return (
					<>
						<Label text={translations.collectText} size="small" className="orange" />
						<Label text={translations.deliverText} size="small" className="green" />
					</>
				);
		}
	}, [wayPointData, translations]);

	const stopNumber = wayPointData.position > numberOfStops ? numberOfStops : wayPointData.position;

	const cardTitle = (
		<div>
			<div className="waypoint-edit-container">
				<div className="waypoint-edit-stops">
					<span>{`${translations.stop} ${stopNumber} ${translations.of} ${numberOfStops}`}</span>

					{getLabel()}
				</div>
				{editable && (
					<div className="waypoint-edit-button">
						<span className="waypoint-edit-link" onClick={onEditClick}>
							{translations.editStop}
							<BringgIcon iconName={BringgFontIcons.Pencil} className="waypoint-edit-link-icon" />
						</span>
					</div>
				)}
			</div>

			<div className="waypoint-address">
				{getFormattedAddress(wayPoint, configuration?.flags?.addressFormat)}
				{validateLocation(wayPointData.lng, wayPointData.lat) && (
					<TooltipWrapper overlayClassName="tooltip-coordinates" title={toolTipText}>
						<span onClick={copyCoordinatesToClipboard}>
							<BringgIcon iconName={BringgFontIcons.Copy} className="copy-icon" />
						</span>
					</TooltipWrapper>
				)}
			</div>
			<div className="waypoint-address-second-line">{wayPoint.address_second_line || ''}</div>
			<div className={'waypoint-status-badge ' + waypointStatusData.className}>{waypointStatusData.title}</div>
		</div>
	);

	const [textualTaskNotes, graphicalTaskNotes] = _partition(taskNotes, note =>
		[NoteType.TaskNote, NoteType.HtmlTaskNote].includes(note.type)
	);

	const rawPhoneNumber = wayPointData.phone || _get(wayPointData, 'customer.phone');
	const phoneNumber = phoneNumberFormatter ? phoneNumberFormatter(rawPhoneNumber) : rawPhoneNumber;

	return (
		<TimezoneContext.Provider value={timeZone}>
			<div className="waypoint-card" data-test-id={wayPointData.id}>
				<CardWrapper title={cardTitle}>
					<Row>
						<Col className="waypoint-card-item" span={16}>
							<WayPointTimes
								scheduledAt={wayPointData.scheduled_at}
								eta={wayPointData.eta}
								promisedTimeWindow={promisedTimeWindow}
								hourFormat={hourFormat}
								noLaterThan={wayPointData.no_later_than}
								noEarlierThan={wayPointData.no_earlier_than}
								etl={wayPointData.etl}
								etos={wayPointData.etos}
								checkinTime={wayPointData.checkin_time}
								checkoutTime={wayPointData.checkout_time}
								timeZone={timeZone}
								translations={translations}
								useTeamTimezone={configuration.flags.useTeamTimezone}
								scheduledArrival={wayPoint.scheduled_arrival}
								setMapMarker={setMapMarker}
								checkinLocation={{ lat: wayPointData.checkin_lat, lng: wayPointData.checkin_lng }}
								checkoutLocation={{ lat: wayPointData.checkout_lat, lng: wayPointData.checkout_lng }}
								asap={wayPointData.asap}
							/>
						</Col>

						<Col className="waypoint-card-item" span={8}>
							<CustomerContact
								name={wayPointData.name || _get(wayPointData, 'customer.name')}
								kind={_get(wayPointData, 'customer.kind')}
								phone={phoneNumber}
								email={wayPointData.email || _get(wayPointData, 'customer.email')}
								verificationCode={showVerificationCode ? wayPointData.verification_pin_code : undefined}
								translations={translations}
							/>
						</Col>
					</Row>

					<WaypointCardTabs
						translations={translations}
						textualTaskNotes={textualTaskNotes}
						onNoteAdded={onNoteAdded}
						hourFormat={hourFormat}
						mapData={mapData}
						tabPaneTitle={tabPaneTitle}
						wayPointData={wayPointData}
						editable={editable}
						canAddNotes={canAddNotes}
						setMapMarker={setMapMarker}
						timezoneForDisplay={timezoneForDisplay}
						graphicalTaskNotes={graphicalTaskNotes}
						scans={scans}
						taskInventories={taskInventories}
						onQuantityChange={onQuantityChange}
						onInventoriesChange={onInventoriesChange}
						editInventoryQuantity={editInventoryQuantity}
						editDimension={editDimension}
						sharedLocations={sharedLocations}
						timeZone={timeZone}
						configuration={configuration}
						allInventoriesInTask={allInventoriesInTask}
						customUnitsDefinition={customUnitsDefinition}
						taskInventoryScanMap={taskInventoryScanMap}
						doNotMultipleWeightByQuantity={doNotMultipleWeightByQuantity}
						shouldShowLinkSourceTask={shouldShowLinkSourceTask}
						defaultTab={defaultTab}
						WindowCommunicationService={WindowCommunicationService}
					/>
				</CardWrapper>
			</div>

			{editable && (
				<Modal
					title={<span className="edit-stop-title">{translations.editStop}</span>}
					className="waypoint-edit-modal"
					data-test-id={`${wayPointData.id}-edit-modal`}
					open={showModalEditForm}
					onOk={hideModalForm}
					onCancel={hideModalForm}
					footer={null}
					centered={true}
					destroyOnClose={true}
				>
					<WayPointCardForm
						wayPoint={wayPointData}
						onCancel={hideModalForm}
						onDelete={onFormDelete}
						onSave={onFormSave}
						translations={translations}
						fetchAddresses={fetchAddresses}
						getPlaceDetails={getPlaceDetails}
						timeZone={timeZone}
						dateFormat={dateFormat}
						hourFormat={hourFormat}
						editBaseEtosAsEtos={editBaseEtosAsEtos}
					/>
				</Modal>
			)}
		</TimezoneContext.Provider>
	);
};

export default WayPointCard;
